// frontend/src/pages/DashboardPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-02
// Updated:      2026-09-23 14:33 BST
//
// Description:  Main configurable dashboard using the shared registry-backed widget renderer
//               while preserving the established dashboard localStorage contract and modal flows.
//
// Desktop resize regression fix (2026-09-23):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence still preserves x/y/w/h and prevents negative y positions.
//   - New widgets are appended below the current layout instead of being forced back inside viewport rows.
//   - Mobile remains a non-editable single-column presentation and never persists mobile layout changes.
//
// Lifecycle Status:
//               Active production dashboard.
//
// Change Summary (2026-09-04):
//   - Added a dedicated mobile dashboard presentation at <= 768px.
//   - Mobile renders the user's selected widgets in a natural single-column flow;
//     react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile ordering follows the persisted desktop layout position (y, then x)
//     without rewriting or maintaining a second mobile layout.
//   - Mobile rendering never persists layout changes, so viewing the dashboard on
//     a phone cannot alter the user's desktop widget arrangement.
//   - Reflowed the page heading and Add Widget action for compact mobile widths.
//   - Reduced mobile page gutters while preserving the existing desktop layout,
//     persistence keys, widget registry integration and modal flows.
//
// Previous Change Summary (2026-08-26):
//   - Removed System Status as the default Main Dashboard widget.
//   - New or reset dashboards now start intentionally empty.
//   - Empty persisted dashboards are now treated as valid saved state and are no longer
//     replaced automatically by a default widget layout.
//   - Removed the obsolete temporary Main Dashboard Job Progress test binding
//     (jobId=1 / TEST_JOB_001).
//   - Preserved registry-backed Add Widget behaviour so authorised/specialist users may
//     still add system, metrics, job, or other widgets explicitly where registered.
//   - Added a lightweight empty-dashboard prompt with no API or widget side effects.
//
// Previous Change Summary (2026-07-30):
//   - Removed direct widget component imports and the page-specific component resolver switch.
//   - Replaced stored React elements with canonical widget-id state resolved by WidgetRenderer.
//   - Removed the redundant `/api/widgets/` catalogue request; registry metadata now supplies titles
//     and the Dashboard picker catalogue.
//   - Added surface-filtered Add Widget behaviour for the `dashboard` presentation surface.
//   - Preserved the existing `dashboardLayout` and `dashboardWidgets` localStorage keys and shapes.
//   - Added deterministic validation, legacy-id migration, de-duplication, and corrupt-state recovery.
//
// Operational Notes:
//   - Dashboard persistence remains page-owned through localStorage.
//   - Existing `weather_spot` storage entries are migrated to canonical `weather_spot_widget`.
//   - Widget components and metadata are resolved exclusively through WidgetRegistry.js.
//   - Main Dashboard has no automatically mounted widgets. This prevents expensive or specialist
//     widgets from generating background API traffic merely because a user opens the dashboard.
//   - Existing user-selected widgets remain preserved when valid and registered for this surface.
//
// Future Intent:
//   - Layer permission/licence-aware widget visibility over the shared widget surface API.
//   - Move dashboard persistence from browser-local storage to the shared preference framework
//     when application-scoped preferences are implemented.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import GridLayout from "react-grid-layout";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import AddWidgetModal from "../components/AddWidgetModal";
import ManageUserModal from "../components/ManageUserModal";
import EditCustomerModal from "../components/EditCustomerModal";
import EditContactModal from "../components/EditContactModal";
import CreateContactModal from "../components/CreateContactModal";

import WidgetRenderer from "../widgets/WidgetRenderer.jsx";

import {
  migrateStoredWidgetState,
  migrateWidgetId,
} from "../widgets/WidgetMigration";

import {
  createSurfaceLayoutItem,
  getSurfaceWidget,
  isWidgetAvailableOnSurface,
} from "../widgets/WidgetSurface";

import { useTheme } from "../context/ThemeContext.jsx";


const SURFACE = "dashboard";

const STORAGE_KEY_LAYOUT = "dashboardLayout";
const STORAGE_KEY_WIDGETS = "dashboardWidgets";

const MOBILE_BREAKPOINT = 768;


function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}


/**
 * The Main Dashboard deliberately has no automatic widgets.
 *
 * Specialist/admin widgets such as System Status and system metrics remain
 * available through the Widget Registry and Add Widget workflow, but simply
 * opening the dashboard must not create monitoring/background API traffic.
 */
const defaultLayout = [];


/**
 * Normalise persisted layout positions without imposing a vertical page ceiling.
 *
 * ReactGridLayout owns desktop widget height. The dashboard is allowed to grow
 * below the initial viewport and the page scrolls naturally.
 */
const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y)
      ? item.y
      : 0;

    return {
      ...item,
      y: Math.max(currentY, 0),
    };
  });


/**
 * Validate the persisted dashboard state.
 *
 * An intentionally empty dashboard is valid:
 *
 *     layout    = []
 *     widgetIds = []
 *
 * Previous behaviour effectively required at least one widget before the
 * stored state could be restored. That made "no widgets" impossible to
 * persist reliably.
 */
const isValidStoredState = (layout, widgetIds) => {
  if (
    !Array.isArray(layout) ||
    !Array.isArray(widgetIds)
  ) {
    return false;
  }

  if (
    widgetIds.some(
      (widgetId) => typeof widgetId !== "string"
    )
  ) {
    return false;
  }

  if (
    layout.some(
      (item) =>
        !item ||
        typeof item !== "object" ||
        typeof item.i !== "string"
    )
  ) {
    return false;
  }

  const canonicalLayoutIds = new Set(
    layout.map(
      (item) => migrateWidgetId(item.i)
    )
  );

  return widgetIds.every(
    (widgetId) =>
      canonicalLayoutIds.has(
        migrateWidgetId(widgetId)
      )
  );
};


const DashboardPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [widgetIds, setWidgetIds] = useState([]);
  const [layout, setLayout] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);

  const [gridWidth, setGridWidth] = useState(1200);

  const [selectedUserId, setSelectedUserId] = useState(null);

  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  const [showContactModal, setShowContactModal] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState(null);

  const gridRef = useRef(null);


  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;


  /**
   * Persist canonical dashboard state.
   */
  const persistState = useCallback(
    (
      nextLayout,
      nextWidgetIds
    ) => {
      try {
        localStorage.setItem(
          STORAGE_KEY_LAYOUT,
          JSON.stringify(nextLayout)
        );

        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify(nextWidgetIds)
        );
      } catch (error) {
        console.warn(
          "Unable to persist Dashboard widget state",
          error
        );
      }
    },
    []
  );


  /**
   * Keep react-grid-layout width synchronised with the available dashboard
   * container width.
   */
  useEffect(() => {
    const node = gridRef.current;

    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      const width = Math.floor(
        node.getBoundingClientRect().width ||
        node.offsetWidth ||
        1200
      );

      if (width > 0) {
        setGridWidth(width);
      }
    };


    updateWidth();


    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;


    if (observer) {
      observer.observe(node);
    }


    window.addEventListener(
      "resize",
      updateWidth
    );


    return () => {
      if (observer) {
        observer.disconnect();
      }

      window.removeEventListener(
        "resize",
        updateWidth
      );
    };
  }, []);


  /**
   * Restore the persisted Dashboard state.
   *
   * Important:
   *   [] / [] is a valid stored dashboard and must remain empty.
   *
   * Invalid widget ids are removed based on the canonical dashboard surface
   * registry. If all previously stored widgets have become unavailable, the
   * resulting dashboard is intentionally empty rather than silently adding
   * another widget.
   */
  useEffect(() => {
    try {
      const stored = migrateStoredWidgetState({
        layoutKey: STORAGE_KEY_LAYOUT,
        widgetsKey: STORAGE_KEY_WIDGETS,
      });


      if (
        isValidStoredState(
          stored.layout,
          stored.widgetIds
        )
      ) {
        const validWidgetIds =
          stored.widgetIds.filter(
            (widgetId) =>
              isWidgetAvailableOnSurface(
                SURFACE,
                widgetId
              )
          );


        const validIdSet =
          new Set(validWidgetIds);


        const validLayout =
          normaliseLayout(
            stored.layout.filter(
              (item) =>
                validIdSet.has(
                  migrateWidgetId(item.i)
                )
            )
          );


        setWidgetIds(validWidgetIds);
        setLayout(validLayout);

        persistState(
          validLayout,
          validWidgetIds
        );

        return;
      }
    } catch (error) {
      console.warn(
        "Dashboard widget storage was invalid and has been reset",
        error
      );

      localStorage.removeItem(
        STORAGE_KEY_LAYOUT
      );

      localStorage.removeItem(
        STORAGE_KEY_WIDGETS
      );
    }


    /**
     * No valid stored configuration exists.
     *
     * Default dashboard is intentionally empty.
     */
    const initialWidgetIds =
      defaultLayout.map(
        (item) => item.i
      );


    setLayout(defaultLayout);
    setWidgetIds(initialWidgetIds);


    persistState(
      defaultLayout,
      initialWidgetIds
    );
  }, [
    persistState,
  ]);


  /**
   * Props required by widgets that need page-owned modal callbacks.
   *
   * Job Progress no longer receives a hard-coded test job from the Main
   * Dashboard. Job-specific state belongs on the Jobs surface.
   */
  const widgetPropsById = useMemo(
    () => ({
      advanced_user_list: {
        setSelectedUserId,
      },

      advanced_contact_list: {
        onEdit: setEditingContact,
      },

      customer_form: {
        onCustomerCreated: (
          customerId
        ) => {
          setNewCustomerId(customerId);
          setShowContactModal(true);
        },
      },

      advanced_customer_list: {
        onEditCustomer:
          setEditingCustomer,
      },
    }),
    []
  );


  /**
   * Add one registry-backed widget to the Main Dashboard.
   */
  const handleAddWidget = useCallback(
    (rawWidgetId) => {
      const widgetId =
        migrateWidgetId(rawWidgetId);


      if (
        !getSurfaceWidget(
          SURFACE,
          widgetId
        )
      ) {
        alert(
          "This widget is not available on the Main Dashboard."
        );

        return;
      }


      if (
        widgetIds.includes(widgetId)
      ) {
        alert(
          "Widget already exists"
        );

        return;
      }


      const bottomY =
        layout.length === 0
          ? 0
          : Math.max(
              ...layout.map(
                (item) =>
                  (item?.y || 0) +
                  (item?.h || 1)
              )
            );


      const newY = Math.max(0, bottomY);


      const newItem =
        createSurfaceLayoutItem(
          SURFACE,
          widgetId,
          {
            x: 0,
            y: newY,
            w: 6,
            h: 4,
            minW: 4,
            minH: 3,
            maxW: 12,
          }
        );


      const nextLayout = [
        ...layout,
        newItem,
      ];


      const nextWidgetIds = [
        ...widgetIds,
        widgetId,
      ];


      setLayout(nextLayout);
      setWidgetIds(nextWidgetIds);


      persistState(
        nextLayout,
        nextWidgetIds
      );


      setShowAddModal(false);
    },
    [
      layout,
      persistState,
      widgetIds,
    ]
  );


  /**
   * Remove a widget and persist the resulting dashboard state.
   *
   * Removing the final widget deliberately persists [] / [].
   */
  const handleRemoveWidget = useCallback(
    (widgetId) => {
      const nextWidgetIds =
        widgetIds.filter(
          (id) => id !== widgetId
        );


      const nextLayout =
        layout.filter(
          (item) => item.i !== widgetId
        );


      setWidgetIds(nextWidgetIds);
      setLayout(nextLayout);


      persistState(
        nextLayout,
        nextWidgetIds
      );
    },
    [
      layout,
      persistState,
      widgetIds,
    ]
  );


  /**
   * Persist drag/resize changes.
   */
  const onLayoutChange = useCallback(
    (newLayout) => {
      const validIds =
        new Set(widgetIds);


      const normalised =
        normaliseLayout(
          newLayout.filter(
            (item) =>
              validIds.has(item.i)
          )
        );


      setLayout(normalised);


      persistState(
        normalised,
        widgetIds
      );
    },
    [
      persistState,
      widgetIds,
    ]
  );


  /**
   * Mobile is a presentation of the existing dashboard configuration, not a
   * second editable layout.
   *
   * Follow the persisted desktop visual order so a user's dashboard remains
   * recognisable on mobile, but never write mobile positioning back to storage.
   */
  const mobileWidgetIds = useMemo(() => {
    const positionById = new Map(
      layout.map((item, index) => [
        item.i,
        {
          y: Number.isFinite(item?.y) ? item.y : Number.MAX_SAFE_INTEGER,
          x: Number.isFinite(item?.x) ? item.x : Number.MAX_SAFE_INTEGER,
          index,
        },
      ])
    );

    return [...widgetIds].sort((a, b) => {
      const pa = positionById.get(a) || {
        y: Number.MAX_SAFE_INTEGER,
        x: Number.MAX_SAFE_INTEGER,
        index: widgetIds.indexOf(a),
      };

      const pb = positionById.get(b) || {
        y: Number.MAX_SAFE_INTEGER,
        x: Number.MAX_SAFE_INTEGER,
        index: widgetIds.indexOf(b),
      };

      if (pa.y !== pb.y) return pa.y - pb.y;
      if (pa.x !== pb.x) return pa.x - pb.x;
      return pa.index - pb.index;
    });
  }, [layout, widgetIds]);


  const getMobileWidgetHeight = useCallback(
    (widgetId) => {
      const item = layout.find(
        (layoutItem) =>
          layoutItem.i === widgetId
      );

      const h =
        Number.isFinite(item?.h)
          ? Math.max(1, item.h)
          : 4;

      /*
       * Approximate react-grid-layout's desktop vertical allocation:
       *   item height = rows * rowHeight + gaps between rows.
       *
       * A 280px floor keeps compact desktop widgets usable on touch screens
       * without forcing every widget into an unnecessarily tall card.
       */
      return Math.max(
        280,
        h * rowHeight +
          Math.max(0, h - 1) * 24
      );
    },
    [layout, rowHeight]
  );


  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        fontFamily: theme.font_body,
        boxSizing: "border-box",
      }}
    >
      <div
        ref={gridRef}
        className="w-full"
        style={{
          minWidth: 0,
        }}
      >
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontFamily: theme.font_header,
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Main Dashboard
          </h2>


          <button
            type="button"
            onClick={() =>
              setShowAddModal(true)
            }
            style={{
              backgroundColor:
                theme.button_action?.normal ||
                "#000",

              color: "#fff",

              padding:
                isMobile
                  ? "10px 14px"
                  : "10px 18px",

              minHeight:
                isMobile
                  ? "44px"
                  : undefined,

              whiteSpace:
                "nowrap",

              borderRadius:
                "6px",

              fontWeight:
                "bold",

              fontFamily:
                "var(--font-body)",

              cursor:
                "pointer",

              border:
                "none",

              transition:
                "background-color 0.2s ease",
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor =
                theme.button_action?.hover ||
                "#333";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor =
                theme.button_action?.normal ||
                "#000";
            }}
            onMouseDown={(event) => {
              event.currentTarget.style.backgroundColor =
                theme.button_action?.click ||
                "#111";
            }}
            onMouseUp={(event) => {
              event.currentTarget.style.backgroundColor =
                theme.button_action?.hover ||
                "#333";
            }}
          >
            Add Widget
          </button>
        </div>


        {widgetIds.length === 0 ? (
          <div
            style={{
              minHeight: isMobile ? "160px" : "180px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed #c7c7c7",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              marginTop: isMobile ? "8px" : "16px",
              padding: isMobile ? "24px 18px" : "32px",
              textAlign: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "8px",
                  fontFamily: theme.font_header,
                }}
              >
                No dashboard widgets configured
              </div>

              <div
                style={{
                  fontSize: "14px",
                  color: "#666666",
                  fontFamily: theme.font_body,
                }}
              >
                Use Add Widget to configure this dashboard.
              </div>
            </div>
          </div>
        ) : isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              minWidth: 0,
              paddingBottom: "8px",
            }}
          >
            {mobileWidgetIds.map(
              (widgetId) => (
                <div
                  key={widgetId}
                  style={{
                    width: "100%",
                    minWidth: 0,
                    height: `${getMobileWidgetHeight(
                      widgetId
                    )}px`,
                    overflow: "hidden",
                  }}
                >
                  <WidgetRenderer
                    widgetId={widgetId}
                    surface={SURFACE}
                    widgetProps={
                      widgetPropsById[
                        widgetId
                      ] || {}
                    }
                    onRemove={() =>
                      handleRemoveWidget(
                        widgetId
                      )
                    }
                  />
                </div>
              )
            )}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            isResizable
            isDraggable
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {widgetIds.map(
              (widgetId) => (
                <div key={widgetId}>
                  <WidgetRenderer
                    widgetId={widgetId}
                    surface={SURFACE}
                    widgetProps={
                      widgetPropsById[
                        widgetId
                      ] || {}
                    }
                    onRemove={() =>
                      handleRemoveWidget(
                        widgetId
                      )
                    }
                  />
                </div>
              )
            )}
          </GridLayout>
        )}


        {showAddModal && (
          <AddWidgetModal
            surface={SURFACE}
            excludedWidgetIds={
              widgetIds
            }
            onClose={() =>
              setShowAddModal(false)
            }
            onSelect={
              handleAddWidget
            }
          />
        )}


        {selectedUserId && (
          <ManageUserModal
            userId={selectedUserId}
            onClose={() =>
              setSelectedUserId(null)
            }
          />
        )}


        {editingCustomer && (
          <EditCustomerModal
            customer={editingCustomer}
            onClose={() =>
              setEditingCustomer(null)
            }
          />
        )}


        {editingContact && (
          <EditContactModal
            contact={editingContact}
            onClose={() =>
              setEditingContact(null)
            }
          />
        )}


        {showContactModal &&
          newCustomerId && (
            <CreateContactModal
              customerId={
                newCustomerId
              }
              onClose={() =>
                setShowContactModal(false)
              }
            />
          )}
      </div>
    </div>
  );
};


export default DashboardPage;
